if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navigation: this.querySelector('[data-gallery-navigation]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initializeNavigation();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === event.detail.currentElement);
        });
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateNavigation(event.detail.currentElement.dataset.mediaId);
      }

      initializeNavigation() {
        if (!this.elements.navigation) return;

        this.elements.navigation.addEventListener('click', this.onNavigationClick.bind(this), { capture: true });
        this.updateNavigation();
      }

      onNavigationClick(event) {
        const button = event.target.closest('.slider-button');
        if (!button || button.disabled) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const mediaItems = this.getNavigableMedia();
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeIndex = mediaItems.indexOf(activeMedia);
        const direction = button.name === 'next' ? 1 : -1;
        const targetMedia = mediaItems[activeIndex + direction];

        if (!targetMedia) return;
        this.setActiveMedia(targetMedia.dataset.mediaId, false);
      }

      getNavigableMedia() {
        if (!this.elements.thumbnails) {
          return [...this.elements.viewer.querySelectorAll('[data-media-id]')];
        }

        return [...this.elements.thumbnails.querySelectorAll('[data-target]')]
          .map((thumbnail) => this.elements.viewer.querySelector(`[data-media-id="${thumbnail.dataset.target}"]`))
          .filter(Boolean);
      }

      updateNavigation(mediaId) {
        if (!this.elements.navigation) return;

        const mediaItems = this.getNavigableMedia();
        const activeMedia = mediaId
          ? this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`)
          : this.elements.viewer.querySelector('[data-media-id].is-active');
        const activeIndex = Math.max(mediaItems.indexOf(activeMedia), 0);
        const previousButton = this.elements.navigation.querySelector('.slider-button--prev');
        const nextButton = this.elements.navigation.querySelector('.slider-button--next');
        const currentCounter = this.elements.navigation.querySelector('.slider-counter--current');
        const totalCounter = this.elements.navigation.querySelector('.slider-counter--total');

        previousButton.disabled = activeIndex === 0;
        nextButton.disabled = activeIndex >= mediaItems.length - 1;
        if (currentCounter) currentCounter.textContent = activeIndex + 1;
        if (totalCounter) totalCounter.textContent = mediaItems.length;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateNavigation(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
